import * as React from 'react'
import { Switch as SwitchPrimitive } from '@base-ui/react/switch'

import { cn } from '@/registry/reapit/lib/utils'

// ---------------------------------------------------------------------------
// Switch — migrated from Radix to Base UI
// ---------------------------------------------------------------------------
// Base UI Switch renders a <span> + hidden <input> (not a <button>).
//
// Data attribute changes:
//   data-state="checked"   → data-checked
//   data-state="unchecked" → data-unchecked
//
// onCheckedChange: Radix `(checked: boolean) => void`
//                → Base UI `(checked: boolean, eventDetails) => void`
// The signatures are compatible (extra arg is ignored by Radix-style callbacks).
// ---------------------------------------------------------------------------

function Switch({
  className,
  size = 'default',
  label,
  labelPosition = 'none',
  id,
  ...props
}: Omit<React.ComponentProps<typeof SwitchPrimitive.Root>, 'render'> & {
  size?: 'sm' | 'default'
  /** DS: label text to display alongside the switch */
  label?: React.ReactNode
  /** DS: position of label relative to switch */
  labelPosition?: 'none' | 'start' | 'end'
}) {
  if (label && labelPosition !== 'none') {
    return (
      <label
        htmlFor={id}
        className={cn(
          'inline-flex cursor-pointer items-center gap-2',
          props.disabled && 'cursor-not-allowed opacity-50',
        )}
      >
        {labelPosition === 'start' && (
          <span data-slot="switch-label" className="text-sm font-medium">
            {label}
          </span>
        )}
        <SwitchRoot size={size} id={id} className={className} {...props} />
        {labelPosition === 'end' && (
          <span data-slot="switch-label" className="text-sm font-medium">
            {label}
          </span>
        )}
      </label>
    )
  }

  return <SwitchRoot size={size} id={id} className={className} {...props} />
}

// DS Switch spec:
//   default track — h-5 (20px) w-9 (36px), thumb size-4 (16px)
//   sm track      — h-4 (16px) w-7 (28px), thumb size-3 (12px)
//
//   Track unchecked: bg-input (neutral-150 #D8DEE4)
//   Track checked:   bg-primary (purple-500 #4E56EA)
//   Thumb:           bg-background (white), translates right when checked
//   Focus ring:      ring-[3px] ring-ring/50 (purple-300)
//   Disabled:        opacity-50, cursor-not-allowed
function SwitchRoot({
  className,
  size = 'default',
  ...props
}: Omit<React.ComponentProps<typeof SwitchPrimitive.Root>, 'render'> & {
  size?: 'sm' | 'default'
}) {
  return (
    <SwitchPrimitive.Root
      data-slot="switch"
      data-size={size}
      className={cn(
        'peer group/switch inline-flex shrink-0 items-center rounded-full border border-transparent shadow-xs transition-all outline-none',
        'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
        'disabled:cursor-not-allowed disabled:opacity-50',
        // Sizes: default h-5 w-9 (20x36px); sm h-4 w-7 (16x28px)
        'data-[size=default]:h-5 data-[size=default]:w-9',
        'data-[size=sm]:h-4 data-[size=sm]:w-7',
        // Track colours — Base UI: data-[checked] / data-[unchecked]
        'data-[checked]:bg-primary data-[unchecked]:bg-input dark:data-[unchecked]:bg-input/80',
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        data-slot="switch-thumb"
        className={cn(
          'pointer-events-none block rounded-full bg-background ring-0 transition-transform',
          // Thumb sizes: default size-4 (16px); sm size-3 (12px)
          'group-data-[size=default]/switch:size-4',
          'group-data-[size=sm]/switch:size-3',
          // Translation: thumb travels (track-width - thumb-size - 2xborder) = (36-16-2)=18px default; (28-12-2)=14px sm
          // Starting position: 2px from left edge (half of 4px total border compensation)
          // Base UI: data-[unchecked] / data-[checked]
          'data-[unchecked]:translate-x-0.5',
          'data-[checked]:group-data-[size=default]/switch:translate-x-[18px]',
          'data-[checked]:group-data-[size=sm]/switch:translate-x-[14px]',
          'dark:data-[checked]:bg-primary-foreground dark:data-[unchecked]:bg-foreground',
        )}
      />
    </SwitchPrimitive.Root>
  )
}

export { Switch }
